import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ProfilePicture from "../../../resources/icons/profile-circle-disabled.svg";
import AuthContext from "../../../store/auth-context";
import { POST, GET } from "../../../service/CRUDService";
import EntityConstants from "../../../configs/EntityConstants";
import Toast from "../../../common/Toast/Toast";
import EmailIcon from "../../../resources/icons/icons8-email-48_purple.svg";
import { FileText, Trash2 } from "lucide-react";
import CfLoader from "../../../common/CfLoader/CfLoader";
import ProfilePicUpload from "../../../common/ProfilePicUpload/ProfilePicUpload";
import { useTranslation } from "react-i18next";
import { pageRootTestIds } from "../../../testing/pageRootTestIds";
import { getProfilePhotoDisplaySrc } from "../../../utils/profilePhoto";
import { indiaPhoneRegex } from "../../../configs/App.Regex";

const GENDER_NOT_SET = "N/A";

const formatApiFieldError = (fieldError) => {
  if (Array.isArray(fieldError)) {
    return fieldError.join(", ");
  }
  if (fieldError == null) {
    return "";
  }
  return String(fieldError);
};

const mapUserToFormData = (userData) => {
  const userGender =
    userData.gender === null || userData.gender === undefined
      ? GENDER_NOT_SET
      : userData.gender;

  return {
    first_name: userData.first_name || "",
    last_name: userData.last_name || "",
    gender: userGender,
    email: userData.email || "",
    date_of_birth: userData.date_of_birth || "",
    phone: userData.phone || "",
  };
};

const Profile = () => {
  const { t } = useTranslation();

  const [userType, setUserType] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [phoneError, setPhoneError] = useState("");
  const [phoneTouched, setPhoneTouched] = useState(false);

  const { createUserProfileDetails, refreshUserSession, userDetails } =
    useContext(AuthContext);
  const fetchRequestIdRef = useRef(0);
  const photoReadIdRef = useRef(0);
  const userDetailsRef = useRef(userDetails);
  userDetailsRef.current = userDetails;
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    gender: GENDER_NOT_SET,
    email: "",
    date_of_birth: "",
    phone: "",
  });
  const [serverProfilePhotoUrl, setServerProfilePhotoUrl] = useState(null);
  const [profilePhotoCache, setProfilePhotoCache] = useState(0);
  const [pendingProfilePhoto, setPendingProfilePhoto] = useState(null);
  const [removeProfilePhoto, setRemoveProfilePhoto] = useState(false);
  const fileInputRef = useRef(null);

  const validatePhoneNumber = useCallback(
    (phone) => {
      if (!phone) {
        return { isValid: false, error: t("profile:phone_required") };
      }

      const isValid = indiaPhoneRegex.test(phone);

      return {
        isValid,
        error: isValid ? "" : t("profile:phone_invalid_example"),
      };
    },
    [t],
  );

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const applyUserDataToForm = useCallback(
    (userData) => {
      const userFormData = mapUserToFormData(userData);
      setUserType(userData.type || "");
      setInitialData(userFormData);
      setFormData(userFormData);
      setServerProfilePhotoUrl(userData.profile_photo || null);
      setProfilePhotoCache(Date.now());
      setPendingProfilePhoto(null);
      setRemoveProfilePhoto(false);
      setPhoneTouched(false);
      clearFileInput();

      if (userFormData.phone) {
        const validation = validatePhoneNumber(userFormData.phone);
        setPhoneError(validation.error);
      } else {
        setPhoneError("");
      }
    },
    [clearFileInput, validatePhoneNumber],
  );

  const needsPhotoReplacement = removeProfilePhoto && !pendingProfilePhoto;

  const activeProfilePhotoUrl =
    pendingProfilePhoto || (removeProfilePhoto ? null : serverProfilePhotoUrl);

  const hasProfilePhoto = Boolean(activeProfilePhotoUrl);

  const profileImageSrc =
    getProfilePhotoDisplaySrc(
      activeProfilePhotoUrl,
      profilePhotoCache,
      ProfilePicture,
    ) || ProfilePicture;

  const profileImageKey = pendingProfilePhoto
    ? "pending"
    : removeProfilePhoto
      ? "removed"
      : `server-${profilePhotoCache}`;

  const isFieldChanged = useMemo(() => {
    const hasFormFieldChanges = Object.keys(formData).some(
      (key) => formData[key] !== initialData[key],
    );
    return (
      hasFormFieldChanges || pendingProfilePhoto !== null || removeProfilePhoto
    );
  }, [formData, initialData, pendingProfilePhoto, removeProfilePhoto]);

  const syncProfileFromApi = useCallback(async () => {
    const response = await GET(EntityConstants.CURRENT_USER);
    if (!response?.ok) {
      throw new Error("Failed to fetch profile");
    }
    const userData = await response.json();
    applyUserDataToForm(userData);
    return userData;
  }, [applyUserDataToForm]);

  useEffect(() => {
    const requestId = ++fetchRequestIdRef.current;

    const loadProfile = async () => {
      try {
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }

        const userData = await syncProfileFromApi();
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }

        const currentUserDetails = userDetailsRef.current;
        if (
          currentUserDetails &&
          Object.keys(currentUserDetails).length === 0
        ) {
          createUserProfileDetails(userData);
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }
        console.error("Error fetching user data:", error);
        Toast({
          type: "error",
          title: t("profile:error"),
          message: t("profile:failed_load_profile"),
        });
      } finally {
        if (requestId === fetchRequestIdRef.current) {
          setIsInitialLoading(false);
        }
      }
    };

    loadProfile();
    // Load profile once on mount; avoid refetch when auth context updates after save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePhoneChange = (event) => {
    let value = event.target.value.replace(/[^\d+]/g, "");

    if (
      value.startsWith("+") &&
      value.length > 3 &&
      value.indexOf(" ") === -1
    ) {
      value = `${value.substring(0, 3)} ${value.substring(3)}`;
    }

    setPhoneTouched(true);
    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));

    const validation = validatePhoneNumber(value);
    setPhoneError(validation.error);
  };

  const handlePhoneBlur = () => {
    setPhoneTouched(true);
    const validation = validatePhoneNumber(formData.phone);
    setPhoneError(validation.error);
  };

  const handleProfilePhotoUpload = (file) => {
    const readId = ++photoReadIdRef.current;
    const reader = new FileReader();

    reader.onload = () => {
      if (readId !== photoReadIdRef.current) {
        return;
      }
      if (typeof reader.result === "string") {
        setPendingProfilePhoto(reader.result);
        setRemoveProfilePhoto(false);
      }
    };

    reader.onerror = () => {
      if (readId !== photoReadIdRef.current) {
        return;
      }
      Toast({
        type: "error",
        title: t("profile:error"),
        message: t("profile:photo_read_error"),
      });
      clearFileInput();
    };

    reader.readAsDataURL(file);
  };

  const handleRemoveProfilePhoto = () => {
    if (pendingProfilePhoto) {
      photoReadIdRef.current += 1;
      setPendingProfilePhoto(null);
      clearFileInput();
      return;
    }

    if (serverProfilePhotoUrl) {
      setRemoveProfilePhoto(true);
      clearFileInput();
    }
  };

  const isFormValid = () => {
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (needsPhotoReplacement) {
      return false;
    }
    return isFieldChanged && phoneValidation.isValid && !isSaving;
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }

    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      setPhoneTouched(true);
      setPhoneError(phoneValidation.error);
      Toast({
        type: "error",
        title: t("profile:validation_error"),
        message: phoneValidation.error,
      });
      return;
    }

    if (needsPhotoReplacement) {
      Toast({
        type: "error",
        title: t("profile:validation_error"),
        message: t("profile:upload_photo_to_save"),
      });
      return;
    }

    const payload = {};

    if (pendingProfilePhoto) {
      payload.profile_photo = pendingProfilePhoto;
    }

    Object.keys(formData).forEach((key) => {
      if (formData[key] !== initialData[key]) {
        payload[key] =
          key === "phone" ? formData[key].replace(/\s/g, "") : formData[key];
      }
    });

    if (Object.keys(payload).length === 0) {
      Toast({
        type: "warning",
        title: t("profile:info"),
        message: t("profile:no_changes_save"),
      });
      return;
    }

    setIsSaving(true);
    try {
      const response = await POST(EntityConstants.CURRENT_USER_UPDATE, payload);
      if (!response) {
        Toast({
          type: "error",
          title: t("profile:error"),
          message: t("profile:failed_update_profile"),
        });
        return;
      }

      let result = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }

      if (response.ok) {
        try {
          const updatedUser = await syncProfileFromApi();
          await refreshUserSession(updatedUser);
          Toast({
            type: "success",
            title: t("profile:success"),
            message: t("profile:profile_update_success"),
          });
        } catch {
          Toast({
            type: "error",
            title: t("profile:error"),
            message: t("profile:failed_load_profile"),
          });
        }
      } else if (result.error) {
        let errorMessage = t("profile:failed_update_profile");

        if (result.error.phone) {
          const phoneMessage = formatApiFieldError(result.error.phone);
          errorMessage = `${t("profile:phone")}: ${phoneMessage}`;
          setPhoneTouched(true);
          setPhoneError(phoneMessage);
        } else if (result.error.profile_photo) {
          errorMessage = formatApiFieldError(result.error.profile_photo);
        } else if (result.message) {
          errorMessage = result.message;
        }

        Toast({
          type: "error",
          title: t("profile:validation_error"),
          message: errorMessage,
        });
      } else {
        Toast({
          type: "error",
          title: t("profile:error"),
          message: result.message || t("profile:failed_update_profile"),
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      Toast({
        type: "error",
        title: t("profile:error"),
        message: t("profile:failed_update_profile"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-[50vh]">
        <CfLoader />
      </div>
    );
  }

  const getSaveButtonTitle = () => {
    if (isSaving) return t("profile:saving");
    if (needsPhotoReplacement) return t("profile:upload_photo_to_save");
    if (!isFieldChanged) return t("profile:make_changes_to_save");
    if (phoneError || !formData.phone) return t("profile:fix_phone_to_save");
    return t("profile:save");
  };

  const canSave = isFormValid();
  const showPhoneError =
    Boolean(phoneError) && (phoneTouched || Boolean(formData.phone));

  const fieldClassName =
    "mt-1.5 border border-cfCardBorder rounded-lg px-3 py-2.5 w-full text-sm text-cfHeadingText placeholder:text-cfGlobalText/60 bg-white focus:outline-none focus:border-cfPrimaryButton focus:ring-2 focus:ring-cfPrimaryButton/20 disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <div
      id={pageRootTestIds.applicantProfile}
      className="flex flex-col w-full mx-auto mt-12 md:mt-20 p-4 bg-cfSecondary min-h-screen"
    >
      <div className="w-full mb-4 flex justify-between items-center gap-4">
        <h1 className="text-xl font-semibold text-cfHeadingText">
          {t("profile:profile")}
        </h1>
        <button
          type="button"
          aria-disabled={!canSave}
          disabled={isSaving}
          onClick={() => {
            if (!canSave) {
              const phoneValidation = validatePhoneNumber(formData.phone);
              if (isFieldChanged && !phoneValidation.isValid) {
                setPhoneTouched(true);
                setPhoneError(phoneValidation.error);
              }
              return;
            }
            handleSave();
          }}
          title={getSaveButtonTitle()}
          className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
            canSave
              ? "bg-cfPrimary hover:bg-cfPrimaryDark"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          {isSaving ? t("profile:saving") : t("profile:save")}
        </button>
      </div>

      <div className="w-full bg-white border border-cfCardBorder rounded-xl shadow-sm">
        <div className="p-5 md:p-6">
          <p className="text-sm font-semibold text-cfHeadingText mb-3">
            {t("profile:photo_section_label")}
          </p>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-5 pb-5 mb-5 border-b border-cfCardBorder">
            <div className="shrink-0 self-center sm:self-auto">
              <img
                key={profileImageKey}
                src={profileImageSrc}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover ring-2 ring-cfCardBorder ring-offset-2 ring-offset-white"
                alt=""
              />
            </div>

            <div className="flex flex-col items-center sm:items-start gap-2 w-fit max-w-full">
              <ProfilePicUpload
                id="fileInput"
                inputRef={fileInputRef}
                heading={
                  hasProfilePhoto
                    ? t("profile:change_profile_photo")
                    : t("profile:upload_profile_photo")
                }
                hint={t("profile:image_upload_hint")}
                errorTitle={t("profile:error")}
                fileSizeErrorMessage={t("profile:file_size_error_5mb")}
                formatErrorMessage={t("profile:image_format_error")}
                uploadErrorMessage={t("profile:photo_upload_error")}
                onFileUpload={handleProfilePhotoUpload}
                disabled={isSaving}
              />
              {hasProfilePhoto && (
                <button
                  type="button"
                  id="remove-profile-photo"
                  onClick={handleRemoveProfilePhoto}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-sm text-cfGlobalText hover:text-cfStatusBlockedText hover:bg-cfErrorBg/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                  {t("profile:remove_profile_photo")}
                </button>
              )}
              {needsPhotoReplacement && (
                <p className="text-xs text-cfGlobalText/80">
                  {t("profile:upload_photo_to_save")}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <div>
              <label
                htmlFor="first_name"
                className="text-sm font-semibold text-cfHeadingText"
              >
                {t("profile:first_name")}
              </label>
              <input
                type="text"
                placeholder={t("profile:first_name")}
                className={fieldClassName}
                name="first_name"
                id="first_name"
                value={formData.first_name}
                onChange={handleChange}
                disabled={isSaving}
              />
            </div>

            <div>
              <label
                htmlFor="last_name"
                className="text-sm font-semibold text-cfHeadingText"
              >
                {t("profile:last_name")}
              </label>
              <input
                type="text"
                placeholder={t("profile:last_name")}
                className={fieldClassName}
                name="last_name"
                id="last_name"
                value={formData.last_name}
                onChange={handleChange}
                disabled={isSaving}
              />
            </div>

            <div>
              <label
                htmlFor="gender"
                className="text-sm font-semibold text-cfHeadingText"
              >
                {t("profile:gender")}
              </label>
              <select
                name="gender"
                id="gender"
                value={
                  formData.gender === GENDER_NOT_SET ? "" : formData.gender
                }
                onChange={handleChange}
                disabled={isSaving}
                className={fieldClassName}
              >
                <option value="" disabled>
                  {t("profile:select_gender")}
                </option>
                <option value="Male">{t("profile:male")}</option>
                <option value="Female">{t("profile:female")}</option>
                <option value="Others">{t("profile:others")}</option>
              </select>
              {formData.gender === GENDER_NOT_SET && (
                <div className="text-cfGlobalText text-sm mt-1">
                  {t("profile:na")}
                </div>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="text-sm font-semibold text-cfHeadingText"
              >
                {t("profile:mobile")}{" "}
                <span className="text-cfStatusBlockedText">*</span>
              </label>
              <input
                type="tel"
                placeholder="+91 9999999999"
                className={`${fieldClassName} ${
                  showPhoneError
                    ? "border-cfStatusBlockedText focus:border-cfStatusBlockedText focus:ring-cfStatusBlockedText/20"
                    : ""
                }`}
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                disabled={isSaving}
                required
                autoComplete="tel"
                aria-invalid={showPhoneError || undefined}
                aria-describedby={showPhoneError ? "phone-error" : undefined}
              />
              {showPhoneError && (
                <p
                  id="phone-error"
                  className="text-cfStatusBlockedText text-xs mt-1"
                >
                  {phoneError}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 w-full sm:w-auto sm:self-start">
        <div className="flex items-center gap-3 bg-white border border-cfCardBorder rounded-xl px-4 py-3 shadow-sm">
          <span className="w-12 h-12 shrink-0 rounded-full bg-cfPrimaryButton flex items-center justify-center">
            <img src={EmailIcon} alt="" className="w-7 h-7" />
          </span>
          <div className="min-w-0 text-sm text-cfHeadingText">
            <p className="flex flex-wrap items-center gap-2">
              <span className="font-semibold truncate">
                {`${formData.first_name} ${formData.last_name}`.trim()}
              </span>
              {userType && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-white bg-cfPrimaryButton">
                  <FileText className="w-3 h-3 text-white" aria-hidden="true" />
                  {userType}
                </span>
              )}
            </p>
            <p className="mt-1 text-cfGlobalText truncate">{formData.email}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
